import { useState, useEffect, useRef } from 'react';
import {
  PaintBrushIcon,
  PhotoIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { brandingAPI, API_URL } from '../../services/api';

// Helper to get full URL for uploaded images
const getImageUrl = (path) => {
  if (!path) return null;
  // If already a full URL, return as-is
  if (path.startsWith('http')) return path;
  // Otherwise prepend API_URL
  return `${API_URL}${path}`;
};

export default function BrandingPage() {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    primaryColor: '',
    secondaryColor: '',
    supportEmail: '',
    emailFromName: '',
  });
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const response = await brandingAPI.get();
      const data = response.data.data;
      setBranding(data);
      setFormData({
        companyName: data.companyName || '',
        primaryColor: data.primaryColor || '#6366f1',
        secondaryColor: data.secondaryColor || '#4f46e5',
        supportEmail: data.supportEmail || '',
        emailFromName: data.emailFromName || '',
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load branding');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await brandingAPI.update(formData);
      setSuccess('Branding updated successfully');
      await fetchBranding();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type = 'logo') => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await brandingAPI.uploadLogo(file, type);
      setSuccess(`${type === 'favicon' ? 'Favicon' : 'Logo'} uploaded successfully`);
      await fetchBranding();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async (type = 'logo') => {
    if (!confirm(`Are you sure you want to remove the ${type}?`)) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await brandingAPI.removeLogo(type);
      setSuccess(`${type === 'favicon' ? 'Favicon' : 'Logo'} removed`);
      await fetchBranding();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove logo');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset all branding to defaults? This will remove your logo and custom colors.')) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await brandingAPI.reset();
      setSuccess('Branding reset to defaults');
      await fetchBranding();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset branding');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Branding</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Customize the look and feel of your workspace with white-label branding
          </p>
        </div>
        <button
          onClick={handleReset}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowPathIcon className="h-5 w-5" />
          Reset to Defaults
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XMarkIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo Upload */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Logo
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Upload your company logo. Recommended size: 200x50px. Max 2MB.
          </p>

          <div className="flex items-center gap-4">
            <div className="w-32 h-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-700">
              {branding?.logoUrl ? (
                <img
                  src={getImageUrl(branding.logoUrl)}
                  alt="Logo"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <PhotoIcon className="h-8 w-8 text-gray-400" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={(e) => handleLogoUpload(e, 'logo')}
                className="hidden"
              />
              <button
                onClick={() => logoInputRef.current?.click()}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                Upload Logo
              </button>
              {branding?.logoUrl && (
                <button
                  onClick={() => handleRemoveLogo('logo')}
                  disabled={saving}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Favicon Upload */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Favicon
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Upload a favicon for browser tabs. Recommended size: 32x32px. Max 2MB.
          </p>

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-700">
              {branding?.faviconUrl ? (
                <img
                  src={getImageUrl(branding.faviconUrl)}
                  alt="Favicon"
                  className="w-8 h-8 object-contain"
                />
              ) : (
                <PhotoIcon className="h-6 w-6 text-gray-400" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={faviconInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={(e) => handleLogoUpload(e, 'favicon')}
                className="hidden"
              />
              <button
                onClick={() => faviconInputRef.current?.click()}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                Upload Favicon
              </button>
              {branding?.faviconUrl && (
                <button
                  onClick={() => handleRemoveLogo('favicon')}
                  disabled={saving}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Branding Settings Form */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Brand Settings
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                placeholder="Your Company Name"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This replaces "CyberActions" throughout the app
              </p>
            </div>

            {/* Email From Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email From Name
              </label>
              <input
                type="text"
                value={formData.emailFromName}
                onChange={(e) => setFormData({ ...formData, emailFromName: e.target.value })}
                placeholder="Your Company Name"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                The "From" name on notification emails
              </p>
            </div>

            {/* Support Email */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Support Email
              </label>
              <input
                type="email"
                value={formData.supportEmail}
                onChange={(e) => setFormData({ ...formData, supportEmail: e.target.value })}
                placeholder="support@yourcompany.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Displayed as the contact email in the app
              </p>
            </div>

            {/* Primary Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Primary Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  className="w-10 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  placeholder="#6366f1"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Main brand color for buttons and accents
              </p>
            </div>

            {/* Secondary Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Secondary Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.secondaryColor}
                  onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                  className="w-10 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.secondaryColor}
                  onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                  placeholder="#4f46e5"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Used for hover states and highlights
              </p>
            </div>
          </div>

          {/* Preview */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Preview
            </h3>
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  style={{ backgroundColor: formData.primaryColor }}
                  className="px-4 py-2 text-white rounded-lg transition-colors"
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = formData.secondaryColor}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = formData.primaryColor}
                >
                  Primary Button
                </button>
                <span className="text-sm" style={{ color: formData.primaryColor }}>
                  {formData.companyName || 'Your Company'}
                </span>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
